import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { apiClient } from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ShapeNode, type ShapeNodeData } from '@/components/fluxograma/ShapeNode';
import type { Fluxograma, FluxogramaEdge as ApiEdge, FluxogramaNode as ApiNode } from '@/types/fluxograma';

type PublicNode = ApiNode & {
  responsavel?: { id: string; nome_customizado: string | null } | null;
  departamento?: { id: string; nome: string } | null;
};

type PublicData = Fluxograma & {
  nodes: PublicNode[];
  edges: ApiEdge[];
};

const tipoLabel: Record<Fluxograma['tipo'], string> = {
  processo: 'Processo',
  bpmn: 'BPMN',
  aprovacao: 'Aprovação',
  customizado: 'Customizado',
};

const nodeTypes = { shape: ShapeNode };

function FluxogramaPublicoInner() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    apiClient
      .get<{ success: boolean; data: PublicData; error?: string }>(`/public/fluxogramas/${token}`)
      .then((res) => {
        if (!res.success) {
          setError(res.error || 'Link inválido ou expirado');
          return;
        }
        setData(res.data);
        const apiNodes = res.data.nodes || [];
        const apiEdges = res.data.edges || [];

        setNodes(
          apiNodes.map((n) => ({
            id: n.id,
            type: 'shape',
            position: { x: n.position_x, y: n.position_y },
            width: n.width,
            height: n.height,
            style: { width: n.width, height: n.height },
            data: {
              label: n.label,
              shape: n.shape,
              width: n.width,
              height: n.height,
              style: n.style ?? null,
              responsavelLabel: n.responsavel?.nome_customizado || null,
              departamentoLabel: n.departamento?.nome || null,
              apiNode: n,
              readonly: true,
            } as ShapeNodeData,
            selectable: false,
            draggable: false,
          })),
        );
        setEdges(
          apiEdges.map((e) => ({
            id: e.id,
            source: e.source_node_id,
            target: e.target_node_id,
            sourceHandle: e.source_handle ?? undefined,
            targetHandle: e.target_handle ? `t-${e.target_handle}` : undefined,
            label: e.label ?? undefined,
            type: e.style_curva === 'step' ? 'step' : e.style_curva === 'straight' ? 'straight' : 'default',
            style: {
              strokeDasharray: e.tipo === 'dashed' ? '6 4' : e.tipo === 'dotted' ? '2 4' : undefined,
              stroke: e.color ?? undefined,
            },
          })),
        );
      })
      .catch(() => setError('Link inválido ou expirado'))
      .finally(() => setLoading(false));
  }, [setEdges, setNodes, token]);

  const headerBadge = useMemo(() => {
    if (!data) return null;
    return (
      <Badge variant="outline" className="text-xs">
        {tipoLabel[data.tipo]}
      </Badge>
    );
  }, [data]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[calc(100vh-180px)] w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto p-6 max-w-md text-center mt-32">
        <h1 className="text-xl font-semibold mb-2">Link indisponível</h1>
        <p className="text-sm text-muted-foreground">{error || 'Este fluxograma não está mais público.'}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <header className="border-b bg-background px-4 py-3 flex items-center gap-3 shrink-0">
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold truncate">{data.nome}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            {headerBadge}
            <span className="text-xs text-muted-foreground">Modo somente leitura</span>
          </div>
        </div>
      </header>
      <div className="flex-1 bg-muted/30">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background
            variant={
              data.canvas_config?.background === 'lines'
                ? BackgroundVariant.Lines
                : data.canvas_config?.background === 'none'
                  ? undefined
                  : BackgroundVariant.Dots
            }
            gap={data.canvas_config?.grid_size ?? 16}
          />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
    </div>
  );
}

export default function FluxogramaPublico() {
  return (
    <ReactFlowProvider>
      <FluxogramaPublicoInner />
    </ReactFlowProvider>
  );
}
